import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { Box, Member, MemberRole } from 'models/Box';
import ManageAccessSkeleton from './ManageAccessSkeleton';
import { styles } from './styles';
import useBoxMembers from 'networking/mindfeed/hooks/useBoxMembers';
import useUpdateBoxMemberRole from 'networking/mindfeed/hooks/useUpdateBoxMemberRole';
import useDeleteBoxMember from 'networking/mindfeed/hooks/useDeleteBoxMember';
import PermissionMenu, { type Permission } from 'modules/PermissionMenu';

type Props = {
  box: Box;
};

const MemberList = ({ box }: Props) => {
  const { data: members, isLoading } = useBoxMembers({
    boxId: box.id,
  });

  const updateBoxMemberRoleMutation = useUpdateBoxMemberRole();
  const deleteBoxMemberMutation = useDeleteBoxMember();

  const handleRoleChange = useCallback(
    async (member: Member, nextPermission: Permission) => {
      const nextRole =
        nextPermission === 'view' ? MemberRole.Viewer : MemberRole.Editor;

      if (member.role === nextRole) return;

      await updateBoxMemberRoleMutation.mutateAsync({
        boxId: box.id,
        memberId: member.id,
        role: nextRole,
      });
    },
    [box, updateBoxMemberRoleMutation],
  );

  const handleRemove = useCallback(
    async (member: Member) => {
      await deleteBoxMemberMutation.mutateAsync({
        boxId: box.id,
        memberId: member.id,
      });
    },
    [box, deleteBoxMemberMutation],
  );

  if (isLoading) {
    return <ManageAccessSkeleton variant="member" />;
  }

  return (
    <>
      {members?.map((member) => (
        <MemberRow
          key={member.id}
          member={member}
          onChange={(next) => handleRoleChange(member, next)}
          onRemove={() => handleRemove(member)}
        />
      ))}
    </>
  );
};

type MemberRowProps = {
  member: Member;
  onChange: (next: Permission) => void;
  onRemove: () => void;
};

const MemberRow = ({ member, onChange, onRemove }: MemberRowProps) => {
  const t = useTranslations();

  const isOwner = member.role === 'owner';
  const permission: Permission = member.role === 'viewer' ? 'view' : 'edit';

  return (
    <div className={styles.owner.container}>
      <div className={styles.owner.left}>
        <div className={styles.owner.avatar} aria-hidden="true" />
        <p className={styles.owner.name}>{member.displayName}</p>
      </div>

      {isOwner ? (
        <p className={styles.owner.role}>{t(`roles.${member.role}`)}</p>
      ) : (
        <PermissionMenu
          permission={permission}
          onChange={onChange}
          onRemove={onRemove}
          showRemove
          showChevronUpWhenOpen
          showCanPrefix
        />
      )}
    </div>
  );
};

export default MemberList;
